import React, { useContext, useEffect, useMemo, RefObject } from 'react';
import styled from '@emotion/styled';
import { BlockConfig, BlockType, UnknownJSON } from '@kizenapps/engine';
import { useCustomBlock } from '@kizenapps/engine/react';
import Loader from 'components/Kizen/Loader';
import { PluginContext } from './context';

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: var(--color-background-white);
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface PluginBlockProps {
  pluginApiName: string | undefined;
  blockApiName: string | undefined;
  instanceId: string | undefined; // Optional instance ID for the block. If provided, the block will be treated as a unique instance and its state will be persisted across renders.
  args?: UnknownJSON;
  surfaceFilter?: BlockType; // Optional filter to only render blocks of a specific type (e.g./ 'record', 'dashboard', etc.)
  uninstalledContent?: React.ReactNode;
  onIsUninstalledChange?: (isUninstalled: boolean) => void;
}

export const PluginBlock = ({
  pluginApiName,
  blockApiName,
  instanceId,
  args = {},
  surfaceFilter,
  uninstalledContent = null,
  onIsUninstalledChange,
}: PluginBlockProps) => {
  const {
    pluginBlocks,
    isLoading: pluginContextLoading,
    isFetched: pluginContextFetched,
    sessionInvalid,
  } = useContext(PluginContext);

  const block = useMemo<BlockConfig | undefined>(() => {
    return pluginBlocks
      .find((entry) => entry.plugin_api_name === pluginApiName)
      ?.blocks.find((b: BlockConfig) => {
        if (b.api_name !== blockApiName) return false;
        if (!surfaceFilter || !b.types?.length) return true;
        return b.types.includes(surfaceFilter);
      });
  }, [pluginBlocks, pluginApiName, blockApiName, surfaceFilter]);

  const {
    scriptUIRef,
    scopedCss,
    sanitizedHtml,
    outputUIRef,
    interactableScriptRef,
    iframeURL,
    pending,
  } = useCustomBlock({
    block,
    args,
    instanceId: instanceId ?? '',
    isLoading: pluginContextLoading,
  });

  const isHydrated = !pluginContextLoading && pluginContextFetched;
  const isUninstalled = isHydrated && pluginApiName && blockApiName && !block;

  useEffect(() => {
    onIsUninstalledChange?.(!!isUninstalled);
  }, [isUninstalled, onIsUninstalledChange]);

  if (sessionInvalid) {
    return null;
  }

  if (!isHydrated || (pending && !block)) {
    return (
      <LoaderOverlay>
        <Loader loading paddingTop="0" />
      </LoaderOverlay>
    );
  }

  if (isUninstalled) {
    return <>{uninstalledContent}</>;
  }

  const blockType = block?.type ?? 'script';

  if (blockType === 'script') {
    return (
      <>
        <div
          ref={scriptUIRef as RefObject<HTMLDivElement>}
          style={{ height: '100%', width: '100%' }}
        />
        <style>{scopedCss}</style>
      </>
    );
  }

  if (blockType === 'html') {
    return (
      <div
        ref={interactableScriptRef as RefObject<HTMLDivElement>}
        style={{ height: '100%', width: '100%', overflow: 'auto' }}
      >
        {sanitizedHtml ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        ) : null}
        <div ref={outputUIRef as RefObject<HTMLDivElement>} />
        <style>{scopedCss}</style>
      </div>
    );
  }

  if (blockType === 'iframe' && iframeURL) {
    return (
      <iframe
        src={iframeURL}
        style={{
          height: '100%',
          width: '100%',
          border: 'none',
          display: 'block',
        }}
        title={block?.name}
        name={pluginApiName}
      />
    );
  }

  return null;
};
